import React, { useState, useContext } from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Container, Button, Modal } from "@mui/material";
import { Construction, ExitToApp } from "@mui/icons-material";
import { useThemeMode } from "../../theme/ThemeContext";
import { ConfirmAction } from "../../assets/controls";
import { useAuth } from "../../services/use-auth";
import UserContext from "../../assets/user_context";
import { signOut } from "firebase/auth";
import { tracedCallable } from "../../utilities/tracedCallable";

// Import logos
import AnimatedLogo from "../../assets/logos/animated-logo.svg";
import AnimatedLogoLight from "../../assets/logos/animated-logo-light.svg";
import LoadingLogo from "../../assets/logos/animated-logo.svg?react";
import LoadingLogoLight from "../../assets/logos/animated-logo-light.svg?react";

const MaintenanceContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  textAlign: "center",
  backgroundColor: theme.palette.background.default,
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  animation: "pulse 2s infinite",
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.05)",
      opacity: 0.8,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  fontWeight: 700,
  color:
    theme.palette.mode === "light"
      ? theme.palette.secondary.main
      : theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  maxWidth: "600px",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.25rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
  },
}));

const StatusText = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2, 3),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: "500px",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& img": {
    width: "120px",
    height: "auto",
    maxWidth: "100%",
  },
}));

const SignOutButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(3),
  right: theme.spacing(3),
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
}));

// Styled components for logout spinner modal
const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "400px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  textAlign: "center",
}));

const LogoutSpinnerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const SpinnerLogo = styled(Box)(({ theme }) => ({
  width: "80px",
  height: "80px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

const MaintenancePage = () => {
  const { mode } = useThemeMode();
  const [openSignOut, setOpenSignOut] = useState(false);
  const [showLogoutSpinner, setShowLogoutSpinner] = useState(false);
  const authHook = useAuth();
  const { org, userData } = useContext(UserContext);

  const signOutFunc = async () => {
    try {
      // Show logout spinner immediately
      setShowLogoutSpinner(true);

      // Track logout event before signing out
      if (authHook.functions && org) {
        try {
          const trackAuthEvent = tracedCallable(
            authHook.functions,
            "trackAuthEvent",
          );
          await trackAuthEvent({
            action: "logout",
            orgId: org,
            metadata: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
              timestamp: new Date().toISOString(),
              context: "maintenance_page",
            },
          });
          console.log("Logout event tracked successfully");
        } catch (trackError) {
          console.error("Failed to track logout event:", trackError);
          // Don't block logout if tracking fails
        }
      }

      await signOut(authHook.auth);
      console.log("User signed out successfully from maintenance page");

      // Hide spinner and close modal
      setShowLogoutSpinner(false);
      setOpenSignOut(false);
    } catch (error) {
      console.error("Error signing out:", error);
      // Hide spinner even on error
      setShowLogoutSpinner(false);
      setOpenSignOut(false);
    }
  };

  // Determine which logo and icon color to use based on theme
  const logoSrc = mode === "light" ? AnimatedLogo : AnimatedLogoLight;
  const iconColor = mode === "light" ? "secondary.main" : "primary.main";

  return (
    <>
      <MaintenanceContainer maxWidth="md" data-testid="maintenance-container">
        {/* Sign Out Button */}
        <SignOutButton
          variant="outlined"
          color={mode === "light" ? "secondary" : "primary"}
          startIcon={<ExitToApp />}
          onClick={() => setOpenSignOut(true)}
          data-testid="maintenance-button-signout">
          Sign Out
        </SignOutButton>

        {/* Animated Logo */}
        <LogoContainer>
          <img src={logoSrc} alt="Dive Logo" />
        </LogoContainer>

        <IconWrapper>
          <Construction
            sx={{
              fontSize: "6rem",
              color: iconColor,
            }}
          />
        </IconWrapper>

        <MainTitle variant="h1">We're Upgrading!</MainTitle>

        <SubTitle variant="h4">
          We're busy rolling out our new version, the app will be back soon.
        </SubTitle>

        <StatusText variant="body1">
          Our team is working hard to bring you exciting new features and
          improvements. We appreciate your patience and will have everything
          back online shortly.
        </StatusText>
      </MaintenanceContainer>

      {/* Logout spinner modal */}
      <Modal
        open={showLogoutSpinner}
        onClose={() => {}} // Prevent closing
        disableEscapeKeyDown>
        <ModalBox>
          <LogoutSpinnerContainer>
            <SpinnerLogo>
              {mode === "dark" ? <LoadingLogoLight /> : <LoadingLogo />}
            </SpinnerLogo>
            <Typography variant="h6">Logging You Out...</Typography>
          </LogoutSpinnerContainer>
        </ModalBox>
      </Modal>

      {/* Sign Out Confirmation Modal */}
      {userData?.username && (
        <ConfirmAction
          dataForAction={{
            label: userData.username,
            actionText: "Sign Out",
          }}
          callback={() => signOutFunc()}
          openState={openSignOut}
          handleCloseState={() => setOpenSignOut(false)}
          useSlider={true}>
          {`Sign Out from Maintenance Mode as ${userData.username}`}
        </ConfirmAction>
      )}
    </>
  );
};

export default MaintenancePage;
